import Icon from 'components/Icon';
import * as consts from 'consts/consts';
import { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Card, CardBody } from 'reactstrap';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import {
  getFriendlyErrorMessageIfErrorOccurred,
  getPrettyDate,
} from 'utils/util/util';
import EmptyState from 'views/Widgets/EmptyState';
import Loading from 'views/Widgets/Loading';
import ConfirmationDialogModal from 'views/Widgets/Modals/ConfirmationDialogModal';
import FilterablePeopleTable from 'views/Widgets/People/FilterablePeopleTable';
import {
  type CustomSurveyReportData,
  formatCustomSurveyReportStatus,
  getCustomSurveyReportStatus,
  type ReportSplitOptions,
} from '../consts';
import CustomSurveyReportCreateModal from './CustomSurveyReportCreateModal';

interface Props {
  surveyId: number;
}

const EMPTY_SPLIT_OPTIONS: ReportSplitOptions = {
  demographic: [],
  leader_levels: [],
  effective_target_filter: null,
};

// Reports tab: list of this survey's reports (owner/admin-only, per
// can_manage_custom_survey_report on the backend), the create flow, and
// delete. Selecting a report (via the edit icon, or right after creating
// one) navigates to its own editor route - the reports tab itself stays a
// plain list, matching CustomSurveysTab's editor-page pattern.
const CustomSurveyReportsTab: FC<Props> = ({ surveyId }) => {
  const { formatMessage, locale } = useIntl();
  const proxyParameters = useProxyParameters();
  const history = useHistory();

  const [reports, setReports] = useState<CustomSurveyReportData[] | undefined>(
    undefined
  );
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingReport, setDeletingReport] =
    useState<CustomSurveyReportData | null>(null);

  const listCallback = useCallback(
    (data: CustomSurveyReportData[] | null, error: unknown) => {
      if (!data || error) return;
      setReports(data);
    },
    []
  );

  const { status } = useConfirmApi<CustomSurveyReportData[]>({
    method: 'GET',
    url: 'custom-survey/reports',
    params: { survey: surveyId },
    callback: listCallback,
  });

  // Only needed once the create modal is open, so this stays lazy rather
  // than fetching split options on every visit to the tab.
  const { data: splitOptions } = useConfirmApi<ReportSplitOptions>({
    method: 'GET',
    url: `custom-survey/surveys/${surveyId}/report-split-options`,
    disabled: !isCreateOpen,
  });

  const handleEditClick = useCallback(
    (reportId: number) => {
      history.push(
        consts
          .ADMINISTRATION_CUSTOM_SURVEYS_REPORT_EDITOR(formatMessage)
          .path.replace(':id', String(surveyId))
          .replace(':reportId', String(reportId))
      );
    },
    [history, formatMessage, surveyId]
  );

  const handleCreate = useCallback(
    (payload: Partial<CustomSurveyReportData>) => {
      ConfirmAPI.sendRequestToConfirm(
        'POST',
        'custom-survey/reports',
        { ...proxyParameters, ...payload },
        (data, error, hardErrorMessage) => {
          if (error || hardErrorMessage) {
            const backendMsg = getFriendlyErrorMessageIfErrorOccurred(
              error,
              hardErrorMessage
            );
            toast.error(
              backendMsg ??
                formatMessage({
                  id: 'app.views.administration.custom_surveys.reports.save_error',
                  defaultMessage: 'Failed to create report.',
                })
            );
            return;
          }
          setReports((prev) => (prev ? [data, ...prev] : [data]));
          setIsCreateOpen(false);
          // Same as creating a survey: go straight to the editor to start
          // building it, rather than leaving the admin on the plain list.
          if (data?.id) {
            handleEditClick(data.id);
          }
        }
      );
    },
    [proxyParameters, formatMessage, handleEditClick]
  );

  const handleDeleteClick = useCallback((report: CustomSurveyReportData) => {
    setDeletingReport(report);
    setIsDeleteModalOpen(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setDeletingReport(null);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!deletingReport) return;

    ConfirmAPI.sendRequestToConfirm(
      'DELETE',
      `custom-survey/reports/${deletingReport.id}`,
      proxyParameters,
      (_data, error, hardErrorMessage) => {
        if (error || hardErrorMessage) {
          const backendMsg = getFriendlyErrorMessageIfErrorOccurred(
            error,
            hardErrorMessage
          );
          toast.error(
            backendMsg ??
              formatMessage({
                id: 'app.views.administration.custom_surveys.reports.delete_error',
                defaultMessage: 'Failed to delete report.',
              })
          );
          return;
        }
        setReports((prev) => prev?.filter((r) => r.id !== deletingReport.id));
        handleCloseDeleteModal();
        toast.success(
          formatMessage({
            id: 'app.views.administration.custom_surveys.reports.delete_success',
            defaultMessage: 'Report deleted.',
          })
        );
      }
    );
  }, [deletingReport, proxyParameters, formatMessage, handleCloseDeleteModal]);

  const statusLabel = useCallback(
    (report: CustomSurveyReportData) =>
      formatCustomSurveyReportStatus(
        getCustomSurveyReportStatus(report),
        formatMessage
      ),
    [formatMessage]
  );

  const columns = useMemo(
    () => [
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.reports.col.name',
          defaultMessage: 'Name',
        }),
        field: 'name',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.reports.col.charts',
          defaultMessage: 'Charts',
        }),
        field: 'charts',
        className: 'col-1 text-center',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.reports.col.audiences',
          defaultMessage: 'Audiences',
        }),
        field: 'audienceCount',
        className: 'col-1 text-center',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.reports.col.status',
          defaultMessage: 'Status',
        }),
        field: 'status',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.reports.col.owner',
          defaultMessage: 'Owner',
        }),
        field: 'owner',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.reports.col.created',
          defaultMessage: 'Created',
        }),
        field: 'created_at',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.reports.col.actions',
          defaultMessage: 'Actions',
        }),
        field: 'actions',
        className: 'col-auto text-center',
        sortable: false,
      },
    ],
    [formatMessage]
  );

  const rows = useMemo(
    () =>
      (reports ?? []).map((r) => ({
        id: r.id,
        name: r.name,
        charts: r.chart_count,
        audienceCount: r.items.length,
        status: statusLabel(r),
        owner: r.owner?.full_name ?? '',
        created_at: getPrettyDate({
          dateString: r.created_at as string,
          includeTime: false,
          locale,
        }),
        actions: (
          <div className="d-flex align-items-center justify-content-center gap-2">
            <button
              className="btn btn-sm btn-light p-1 px-2 me-1"
              onClick={() => handleEditClick(r.id)}
              aria-label={formatMessage({
                id: 'app.views.administration.custom_surveys.reports.edit',
                defaultMessage: 'Edit report',
              })}
            >
              <Icon icon={consts.ICONS.EDIT} />
            </button>
            <Button
              color="link"
              className="p-0 text-danger"
              onClick={() => handleDeleteClick(r)}
              aria-label={formatMessage({
                id: 'app.views.administration.custom_surveys.reports.delete',
                defaultMessage: 'Delete report',
              })}
            >
              <Icon icon={consts.ICONS.TRASH} />
            </Button>
          </div>
        ),
      })),
    [
      reports,
      statusLabel,
      handleDeleteClick,
      handleEditClick,
      formatMessage,
      locale,
    ]
  );

  const createButton = (
    <div className="d-flex justify-content-end mb-3">
      <Button color="primary" onClick={() => setIsCreateOpen(true)}>
        <FormattedMessage
          id="app.views.administration.custom_surveys.reports.create"
          defaultMessage="Create report"
        />
      </Button>
    </div>
  );

  if (status === 'LOADING') {
    return <Loading />;
  }

  if (status === 'ERROR' || !reports) {
    return (
      <Card>
        <CardBody>
          <EmptyState
            title={formatMessage({
              id: 'app.views.administration.custom_surveys.reports.load_error',
              defaultMessage: 'Could not load reports.',
            })}
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      {createButton}

      {reports.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState
              title={formatMessage({
                id: 'app.views.administration.custom_surveys.reports.empty',
                defaultMessage:
                  'No reports yet. Create a report to group charts and share them.',
              })}
            />
          </CardBody>
        </Card>
      ) : (
        <FilterablePeopleTable
          columns={columns}
          rows={rows}
          hideFilters={true}
          hideExportButton={true}
          hideHeader={true}
        />
      )}

      <CustomSurveyReportCreateModal
        isOpen={isCreateOpen}
        toggle={() => setIsCreateOpen(false)}
        surveyId={surveyId}
        splitOptions={splitOptions ?? EMPTY_SPLIT_OPTIONS}
        onCreate={handleCreate}
      />

      {isDeleteModalOpen && deletingReport && (
        <ConfirmationDialogModal
          isOpen={isDeleteModalOpen}
          onClosed={handleCloseDeleteModal}
          toggle={handleCloseDeleteModal}
          confirmCallback={handleDeleteConfirm}
          title={formatMessage({
            id: 'app.views.administration.custom_surveys.reports.delete.title',
            defaultMessage: 'Delete report',
          })}
          description={formatMessage(
            {
              id: 'app.views.administration.custom_surveys.reports.delete_confirm',
              defaultMessage:
                'Delete "{name}"? This removes its audiences and charts too. This cannot be undone.',
            },
            { name: deletingReport.name }
          )}
          confirmText={formatMessage({
            id: 'app.views.administration.custom_surveys.reports.delete.confirm',
            defaultMessage: 'Delete',
          })}
        />
      )}
    </>
  );
};

export default CustomSurveyReportsTab;
